import { Typography } from '@global-engineering-shared/gweb-material-ui';
import React, { ChangeEvent, useRef, useState } from 'react';

import ImportChipList from 'features/Import/components/ImportDialog/components/ImportChipTextInput/components/ImportChipList';
import useStyles from 'features/Import/components/ImportDialog/components/ImportChipTextInput/ImportChipTextInput.styles';
import {
  addChip,
  removeLastChip,
} from 'features/Import/components/ImportDialog/components/ImportChipTextInput/utils/chipHelpers';
import { BACKSPACE, COMMA, ENTER, SPACE } from 'utils/defaults';

const SPACE_AND_COMMAS = /[\s,]+/;
const EMPTY_STRING = '';
const MAX_VISIBLE_CHIPS = 20;
const CHIPS_PER_PAGE = 20;

type ImportChipTextInputProps = {
  onChipsChange: (chips: string[]) => void;
  placeholder: string;
};

const ImportChipTextInput = ({ onChipsChange, placeholder }: ImportChipTextInputProps) => {
  const classes = useStyles();
  const [chips, setChips] = useState<string[]>([]);
  const [visibleChipsCount, setVisibleChipsCount] = useState<number>(CHIPS_PER_PAGE);
  const [inputValue, setInputValue] = useState<string>(EMPTY_STRING);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const { key } = e;
    if ([ENTER, SPACE, COMMA].includes(key)) {
      addChip(chips, setChips, onChipsChange, inputValue);
      setInputValue(EMPTY_STRING);
    } else if (key === BACKSPACE && inputValue === EMPTY_STRING) {
      removeLastChip(chips, setChips, onChipsChange);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (SPACE_AND_COMMAS.test(input)) {
      const newChips = input.split(SPACE_AND_COMMAS).filter(Boolean);
      const trimmedChips = newChips.map((chip) => chip.trim());
      setChips((prevChips) => {
        const allChips = [...prevChips, ...trimmedChips];
        onChipsChange(allChips);
        return allChips;
      });
      setInputValue(EMPTY_STRING);
    } else {
      setInputValue(input);
    }
  };

  const handleOnDelete = (index: number) => {
    const newChips = chips.filter((_, i) => i !== index);
    setChips(newChips);
    onChipsChange(newChips);
  };

  const showMoreChips = () => {
    setVisibleChipsCount((prevCount) => prevCount + CHIPS_PER_PAGE);
  };

  return (
    <div className={classes.root}>
      <Typography variant="label" color="textPrimary">
        SPACE ID
      </Typography>
      <div className={classes.chipContainer}>
        <ImportChipList
          chips={chips}
          visibleChipsCount={visibleChipsCount}
          maxVisibleChips={MAX_VISIBLE_CHIPS}
          handleOnDelete={handleOnDelete}
          showMoreChips={showMoreChips}
        />
        <input
          className={classes.input}
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={chips.length > 0 ? EMPTY_STRING : placeholder}
          autoFocus
        />
      </div>
    </div>
  );
};

export default ImportChipTextInput;
